'use client'

import { cn } from '@/lib/utils'
import { motion } from 'framer-motion'
import * as React from 'react'

interface EnhancedInputProps extends React.ComponentProps<'input'> {
	label?: string
	error?: string
}

/**
 * EnhancedInput
 * Animated input field using design tokens from platform.config.json.
 * Consistent with the SINK-O visual language: clean, flat, bordered.
 */
const EnhancedInput = React.forwardRef<HTMLInputElement, EnhancedInputProps>(
	({ className, type, label, error, ...props }, ref) => {
		return (
			<motion.div
				initial={{ opacity: 0, y: 10 }}
				animate={{ opacity: 1, y: 0 }}
				className="flex flex-col gap-1.5"
			>
				{label && <label className="text-muted-foreground text-sm font-medium">{label}</label>}
				<input
					type={type}
					className={cn(
						'border-input bg-card flex h-11 w-full rounded-lg border px-4 py-2.5',
						'text-foreground placeholder:text-muted-foreground text-sm',
						'focus-visible:ring-ring focus-visible:border-ring transition-colors focus-visible:ring-1 focus-visible:outline-none',
						'disabled:cursor-not-allowed disabled:opacity-50',
						error && 'border-destructive focus-visible:ring-destructive',
						className
					)}
					ref={ref}
					{...props}
				/>
				{error && (
					<motion.p
						initial={{ opacity: 0, y: -4 }}
						animate={{ opacity: 1, y: 0 }}
						className="text-destructive text-xs"
					>
						{error}
					</motion.p>
				)}
			</motion.div>
		)
	}
)

EnhancedInput.displayName = 'EnhancedInput'

export { EnhancedInput }
